import { Button, Card, Modal, Table, Row, Col, Dropdown } from "antd";
import axios from "axios";
import { useViewport } from "@bindeleddet/core/hooks";
import MobileTable from "components/MobileTable/MobileTable";
import dayjs from "dayjs";
import { message } from "@bindeleddet/core/utils";
import { LogEntry, NoShow } from "@bindeleddet/core/types";
import React, { useEffect, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { EllipsisOutlined } from "@ant-design/icons";

interface Props {
  studentId: number;
  onTermViolationCreated: (noShow: NoShow) => void;
}

const Log: React.FC<Props> = ({ studentId, onTermViolationCreated }) => {
  const [log, setLog] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [unregisterLoading, setUnregisterLoading] = useState<boolean>(false);
  const [termViolationLoading, setTermViolationLoading] = useState<boolean>(false);
  const { width } = useViewport();
  const history = useHistory();

  const deregister = (student: number, event: number, event_name: string) => {
    Modal.confirm({
      title: "Meld av arrangement",
      content: `Er du sikker på at du vil melde studenten av ${event_name}?`,
      okText: "Meld av",
      cancelText: "Avbryt",
      okButtonProps: {
        danger: true,
        loading: unregisterLoading,
      },
      onOk() {
        setUnregisterLoading(true);
        return axios
          .post(`/events/${event}/admin_unregister/`, { student })
          .then(() => {
            setLog(log.filter(entry => entry.event !== event));
            message("success", "Studenten er meldt av arrangementet");
            setUnregisterLoading(false);
          })
          .catch(() => {
            message("error", "Feil ved avmelding av arrangement");
            setUnregisterLoading(false);
          });
      },
    });
  };

  const createTermViolation = (student: number, event: number, event_name: string) => {
    Modal.confirm({
      title: "Gi prikk",
      content: `Er du sikker på at du vil gi studenten prikk for arrangementet "${event_name}"?`,
      okText: "Gi prikk",
      cancelText: "Avbryt",
      okButtonProps: {
        danger: true,
        loading: termViolationLoading,
      },
      onOk() {
        setTermViolationLoading(true);
        return axios
          .post<NoShow>(`/events/${event}/create-term-violation/`, { student })
          .then(response => {
            message("success", "Prikk er gitt til studenten");
            setTermViolationLoading(false);
            onTermViolationCreated(response.data);
          })
          .catch(() => {
            message("error", "Feil ved gi prikk til studenten");
            setTermViolationLoading(false);
          });
      },
    });
  };

  useEffect(() => {
    setLoading(true);
    axios
      .get<LogEntry[]>(`/events/${studentId}/log/`)
      .then(response => {
        setLog(response.data);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  }, [studentId]);

  const columns = [
    {
      title: "Dato",
      dataIndex: "event_start",
      key: "event_start",
      render: (event_start: string) => dayjs(event_start).format("DD.MM.YYYY"),
    },
    {
      title: "Arrangement",
      dataIndex: "event_name",
      key: "event",
      render: (event_name: string, record: LogEntry) => {
        return <Link to={`/admin/events/${record.event}/`}>{event_name}</Link>;
      },
    },
    {
      title: "Deltok",
      dataIndex: "time_attended",
      key: "time_attended",
      render: (time_attended: number) => (time_attended ? "Ja" : "Nei"),
    },
    {
      title: "",
      dataIndex: "event",
      key: "event",
      render: (_: number, record: LogEntry) => {
        const has_started = dayjs().isAfter(dayjs(record.event_start));
        const menuItems = has_started
          ? [
              {
                key: "no-show",
                danger: true,
                label: "Gi prikk",
                onClick: () => createTermViolation(record.student, record.event, record.event_name),
              },
            ]
          : [
              {
                key: "deregister",
                danger: true,
                label: "Meld av",
                onClick: () => deregister(record.student, record.event, record.event_name),
              },
            ];

        return (
          <Row style={{ justifyContent: "end" }} gutter={[5, 5]}>
            <Col>
              <Dropdown menu={{ items: menuItems }}>
                <Button icon={<EllipsisOutlined />} />
              </Dropdown>
            </Col>
          </Row>
        );
      },
    },
  ];
  return (
    <Card title="Påmeldte arrangementer">
      {width > 768 ? (
        <Table
          dataSource={log}
          loading={loading}
          columns={columns}
          locale={{ emptyText: "Studenten har ikke meldt seg på noen arrangementer" }}
        />
      ) : (
        <MobileTable
          data={log}
          columns={[
            {
              title: "Dato",
              dataIndex: "event_start",
              key: "event_start",
              render: event_start =>
                new Date(event_start as string).toLocaleString("nb-NO", {
                  year: "numeric",
                  month: "2-digit",
                  day: "2-digit",
                  hour: "2-digit",
                  minute: "2-digit",
                }),
            },
            {
              title: "Arrangement",
              dataIndex: "event_name",
              key: "event_name",
            },
            {
              title: "Deltok",
              dataIndex: "time_attended",
              key: "time_attended",
              render: time_attended => (time_attended ? "Ja" : "Nei"),
            },
          ]}
          loading={loading}
          onClick={(_, record) => history.push(`/admin/events/${record.event}/`)}
          emptyText="Studenten har ikke meldt seg på noen arrangementer"
          actions={record => {
            const has_started = dayjs().isAfter(dayjs(record.event_start));
            const menuItems = has_started
              ? [
                  {
                    key: "no-show",
                    danger: true,
                    label: "Gi prikk",
                    onClick: () => createTermViolation(record.student, record.event, record.event_name),
                  },
                ]
              : [
                  {
                    key: "deregister",
                    danger: true,
                    label: "Meld av",
                    onClick: () => deregister(record.student, record.event, record.event_name),
                  },
                ];
            return (
              <Dropdown menu={{ items: menuItems }}>
                <Button icon={<EllipsisOutlined />} />
              </Dropdown>
            );
          }}
        />
      )}
    </Card>
  );
};

export default Log;
